import { newDescriptionPanelId } from './DescriptionPanelId';
import type { DescriptionPanelId, ModelId, ViewModelId, DescriptionPanelJSON } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { Size } from '@view-model/models/common/basic';
import { ObjectRepository } from '@framework/repository';
import type { ICommand } from '@model-framework/command';
import { CreateCommand } from '@model-framework/command';
import { Timestamp } from '@framework/Timestamp';
import { DescriptionPanelFontSize } from './DescriptionPanelFontSize';
import { ModelLayout } from '@view-model/models/sticky/layout';
import { ViewModelAssetURL } from '@user/pages/urls';
import { MarkdownParser } from '@framework/markdown';
import type { AssetUrlMap } from '@view-model/domain/view-model';
import { HtmlParser } from '@framework/html';

export class DescriptionPanel {
  static DefaultSize = new Size(ModelLayout.GridSize * 20, ModelLayout.GridSize * 12);
  static MinimumSize = new Size(ModelLayout.GridSize * 4, ModelLayout.GridSize * 4);

  constructor(
    public readonly id: DescriptionPanelId,
    public readonly description: string,
    public readonly htmlDescription: string,
    public readonly size: Size,
    public readonly createdAt: Timestamp,
    public readonly fontSize: DescriptionPanelFontSize
  ) {
    this.fontSize = DescriptionPanelFontSize.load(fontSize);
  }

  dump(): DescriptionPanelJSON {
    const { id, description, htmlDescription, size, createdAt, fontSize } = this;
    return {
      id,
      description,
      htmlDescription,
      size: size.dump(),
      createdAt: createdAt.toUnixTimestamp(),
      fontSize: DescriptionPanelFontSize.dump(fontSize),
    };
  }

  static load(dump: DescriptionPanelJSON): DescriptionPanel {
    const { id, description, htmlDescription, size, createdAt, fontSize } = dump;
    return new DescriptionPanel(
      id,
      description,
      htmlDescription,
      Size.load(size),
      Timestamp.fromUnixTimestamp(createdAt),
      DescriptionPanelFontSize.load(fontSize)
    );
  }

  static buildNew(description?: string, htmlDescription?: string): DescriptionPanel {
    const id = newDescriptionPanelId();
    return new DescriptionPanel(
      id,
      description || '',
      htmlDescription || '',
      DescriptionPanel.DefaultSize,
      Timestamp.now(),
      DescriptionPanelFontSize.S
    );
  }

  isEqual(other: DescriptionPanel): boolean {
    return (
      this.id === other.id &&
      this.description === other.description &&
      this.htmlDescription === other.htmlDescription &&
      this.size.isEqual(other.size) &&
      this.fontSize == other.fontSize
    );
  }

  withDescription(description: string, htmlDescription: string): DescriptionPanel {
    const { id, size, createdAt, fontSize } = this;
    return new DescriptionPanel(id, description, htmlDescription, size, createdAt, fontSize);
  }

  cloneNew(newId: DescriptionPanelId, createdAt: Timestamp, assetUrlMap: AssetUrlMap): DescriptionPanel {
    const { description, htmlDescription, size, fontSize } = this;
    return new DescriptionPanel(
      newId,
      MarkdownParser.replaceUrls(description, assetUrlMap),
      HtmlParser.replaceUrls(htmlDescription, assetUrlMap),
      size,
      createdAt,
      fontSize
    );
  }

  withSize(size: Size): DescriptionPanel {
    const { id, description, htmlDescription, createdAt, fontSize } = this;
    return new DescriptionPanel(id, description, htmlDescription, size, createdAt, fontSize);
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    const repository = new ObjectRepository(
      DescriptionPanel,
      RTDBPath.DescriptionPanel.panelPath(viewModelId, modelId, this.id)
    );
    return new CreateCommand(this, repository);
  }

  /**
   * 説明パネルのマークダウンを解析して、埋め込まれた view-model-assets の URL 一覧を返します。
   */
  assetUrls(): string[] {
    const tokens = MarkdownParser.filterByUrl(
      (urlString) => !!ViewModelAssetURL.fromURLString(urlString),
      MarkdownParser.parse(this.description)
    );
    const urls = tokens.map((token) => token.url as string);
    return Array.from(new Set(urls));
  }
}
